import { motion, animate } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { Wrench, Calendar, Users, MapPin } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";

function AnimatedCounter({ target, suffix = "", prefix = "" }: { target: number; suffix?: string; prefix?: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          const controls = animate(0, target, {
            duration: 2,
            ease: "easeOut",
            onUpdate: (v) => setCount(Math.floor(v)),
          });
          return () => controls.stop();
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, hasAnimated]);

  return (
    <div ref={ref} className="font-display font-extrabold text-4xl md:text-5xl text-primary-deep">
      {prefix}{count.toLocaleString()}{suffix}
    </div>
  );
}

export default function StatsSection() {
  const { t } = useLanguage();

  const stats: { icon: LucideIcon; value: number; suffix: string; labelKey: string }[] = [
    { icon: Wrench, value: 10, suffix: "+", labelKey: "serviceAreas" },
    { icon: Calendar, value: 6, suffix: "", labelKey: "dayWorkWeek" },
    { icon: Users, value: 100, suffix: "%", labelKey: "projectFocused" },
    { icon: MapPin, value: 1, suffix: "", labelKey: "riyadhBase" },
  ];

  return (
    <section className="bg-white py-16 md:py-20 px-4 sm:px-6 lg:px-8 border-y border-muted-border">
      <div className="content-container">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
          {stats.map((stat, i) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.labelKey}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12, duration: 0.5 }}
                className="text-center"
              >
                <div className="w-12 h-12 rounded-xl bg-primary-deep/5 flex items-center justify-center mx-auto mb-4">
                  <Icon className="w-5 h-5 text-primary-deep" />
                </div>
                <AnimatedCounter target={stat.value} suffix={stat.suffix} />
                <p className="text-sm text-steel mt-2">{t(stat.labelKey)}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
